import { useState, useEffect, Fragment } from "react";
import { Input, Table, Button, Popover, Space } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useRequest } from "ahooks";
import { Link } from "react-router-dom";
import { useAuthContext } from "@/components/auth/AuthProvider";
import AdministrativeTreeSelect from "@/components/common/AdministrativeTreeSelect";
import { department as departmentService } from "@/services";
import DepartmentAddButton from "./DepartmentAddButton";
import DepartmentUserButton from "./DepartmentUserButton";
import DepartmentEditButton from "./DepartmentEditButton";
import DepartmentDeleteButton from "./DepartmentDeleteButton";

function DepartmentTable({ departmentType }) {
  const [version, setVersion] = useState(Date.now());
  const [administrative, setAdministrative] = useState();
  const [keyword, setKeyword] = useState();
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const auth = useAuthContext();

  const {
    data = { total: 0, results: [] },
    loading,
    run,
  } = useRequest(
    (params, token) => {
      return departmentService.list(params, token);
    },
    {
      manual: true,
      throttleWait: 600,
    }
  );

  useEffect(() => {
    setPageSize(50);
    setPage(1);
  }, [departmentType]);

  useEffect(() => {
    if (auth) {
      run(
        {
          version,
          administrative,
          search: keyword,
          page,
          page_size: pageSize,
          types: departmentType === "all" ? undefined : departmentType,
        },
        auth.token
      );
    }
  }, [
    run,
    keyword,
    page,
    pageSize,
    auth,
    departmentType,
    version,
    administrative,
  ]);

  const columns = [
    {
      title: "单位名称",
      dataIndex: "name",
      render: (text, record) => <Link to={`${record.id}`}>{text}</Link>,
    },
    {
      title: "负责人信息",
      key: "director",
      render: (record) => {
        const content = (
          <ul style={{ margin: 15, padding: 0 }}>
            <li>姓名： {record.director_name}</li>
            <li>职务： {record.director_job}</li>
            <li>电话： {record.director_phone}</li>
          </ul>
        );
        return (
          <Popover content={content}>
            <Button type="link">{record.director_name}</Button>
          </Popover>
        );
      },
    },
    {
      title: "联系人信息",
      key: "linkman",
      render: (record) => {
        if (record.employees.length === 0) return "";
        const content = (
          <ul style={{ marginLeft: 16, padding: 0 }}>
            {record.employees.map((employee, index) => (
              <Fragment key={`linkman-${index}`}>
                <li>登录账号： {employee.user.username}</li>
                <li>姓名： {employee.name}</li>
                <li>职务： {employee.job}</li>
                <li>电话： {employee.phone}</li>
              </Fragment>
            ))}
          </ul>
        );
        return (
          <Popover content={content}>
            <Button type="link">
              {record.employees[0].name || record.employees[0].user.username}
            </Button>
          </Popover>
        );
      },
    },
    {
      title: "操作",
      key: "operate",
      width: 120,
      render: (record) => (
        <Space>
          <DepartmentUserButton
            token={auth.token}
            departmentName={record.name}
            departmentId={record.id}
            callback={setVersion}
          />
          <DepartmentEditButton
            token={auth.token}
            departmentId={record.id}
            roles={auth.roles}
            callback={setVersion}
          />
          <DepartmentDeleteButton
            token={auth.token}
            departmentId={record.id}
            callback={setVersion}
          />
        </Space>
      ),
    },
  ];

  return (
    <div className="table-container">
      <div className="table-toolbar">
        <div className="left">
          <DepartmentAddButton
            token={auth.token}
            roles={auth.roles}
            callback={setVersion}
          />
        </div>
        <div className="right">
          <Space>
            <div>
              行政区划：
              <AdministrativeTreeSelect
                token={auth.token}
                onChange={(v) => setAdministrative(v)}
              />
            </div>
            <div>
              名称：
              <Input
                prefix={<SearchOutlined style={{ color: "#ccc" }} />}
                style={{ width: 220 }}
                placeholder="搜索内容"
                allowClear
                onChange={(e) => setKeyword(e.target.value)}
              />
            </div>
          </Space>
        </div>
      </div>
      <Table
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={data.results}
        loading={loading}
        pagination={{
          pageSize: pageSize,
          total: data.count,
        }}
        onChange={({ current, pageSize }) => {
          setPage(current);
          setPageSize(pageSize);
        }}
      />
    </div>
  );
}

export default DepartmentTable;
